/**
 * Pidcat / Chrome DevTools-style "consecutive duplicate folding".
 *
 * When the same parsed line (same pid + tag + level + message) arrives N
 * times in a row, we collapse it into one display row carrying a `× N`
 * counter. This is purely a *presentation* transform — the raw ring buffer
 * is untouched, so save / export / event detection still see every line.
 *
 * Why consecutive-only (vs. global dedup):
 *   - Preserves chronological order. A repeated message that re-appears
 *     5 minutes later starts a new group instead of silently bumping an
 *     old one out of place.
 *   - O(n) single pass, no extra hash map churn per render.
 *   - Matches how `uniq -c` and DevTools console grouping behave; users
 *     already have a mental model.
 *
 * Keying rationale:
 *   - `pid` is included so the same boilerplate from two processes
 *     (e.g. `dalvikvm`) doesn't fold across them.
 *   - `tid` is intentionally **not** included; threads within a process
 *     emitting the same chatter (e.g. GC) are usually the same logical
 *     event and folding them is what the user actually wants.
 *   - `ts` is obviously excluded — timestamps are by definition unique
 *     and would defeat the whole point.
 */
import type { LogLine } from './parser';

export interface DisplayRow extends LogLine {
  /** Number of source lines folded into this row (>= 1). */
  count: number;
  /** Timestamp of the first occurrence in the run. */
  firstTs: string;
  /** Timestamp of the latest occurrence in the run. */
  lastTs: string;
  /** Inclusive index range in the source array this row represents. */
  srcStart: number;
  srcEnd: number;
}

/**
 * Aggregate `lines` by collapsing consecutive runs of duplicates into
 * a single `DisplayRow` with a count. Non-duplicate lines pass through
 * with `count = 1`.
 *
 * Optional `prev`: previous output for the same (growing) `lines` array.
 * Rows that have been finalised (i.e. the line *after* their `srcEnd` is
 * a different line that "seals" the run) are reused **by reference** in
 * the new output. Only the unsealed trailing row and any new tail need
 * re-allocation.
 *
 * Why this matters: React.memo on the row component can then bail out
 * for the vast majority of visible rows on each stream tick. Without
 * stable identity, every rAF cycle re-creates every row's DOM and the
 * browser drops the user's text selection (and tanks scroll smoothness).
 */
export function aggregateConsecutive(
  lines: LogLine[],
  prev?: readonly DisplayRow[]
): DisplayRow[] {
  let reusedCount = 0;
  let cursor = 0;

  if (prev && prev.length > 0 && lines.length > 0) {
    const first = prev[0];
    const head = lines[first.srcStart];
    // Cheap sanity check: alignment hasn't shifted (e.g. ring eviction).
    if (
      first.srcStart === 0 &&
      head &&
      head.pid === first.pid &&
      head.tag === first.tag &&
      head.level === first.level &&
      head.message === first.message
    ) {
      // A prev row is "sealed" iff the line immediately after its
      // `srcEnd` exists *and* is not a duplicate of it — then it can
      // never grow and is safe to reuse as-is. The very last prev row
      // is also reusable when no new lines have arrived yet.
      for (let i = 0; i < prev.length; i++) {
        const r = prev[i];
        if (r.srcEnd >= lines.length) break; // line was evicted
        const after = lines[r.srcEnd + 1];
        if (after) {
          if (isDupLine(after, r)) break; // would need to grow
          reusedCount = i + 1;
        } else {
          reusedCount = i + 1;
          break;
        }
      }
      if (reusedCount > 0) cursor = prev[reusedCount - 1].srcEnd + 1;
    }
  }

  const out: DisplayRow[] = [];
  for (let i = 0; i < reusedCount; i++) out.push(prev![i]);

  for (let i = cursor; i < lines.length; i++) {
    const l = lines[i];
    const last = out.length > 0 ? out[out.length - 1] : undefined;
    // Never mutate a row that came from `prev` — must keep object identity
    // stable for React.memo. When that's the case, the new line always
    // starts a fresh row even if it would technically fold.
    const lastIsReused = out.length <= reusedCount;
    if (last && !lastIsReused && isDup(last, l)) {
      last.count++;
      last.lastTs = l.ts;
      last.srcEnd = i;
    } else {
      out.push({
        ...l,
        count: 1,
        firstTs: l.ts,
        lastTs: l.ts,
        srcStart: i,
        srcEnd: i,
      });
    }
  }
  return out;
}

function isDupLine(line: LogLine, row: DisplayRow): boolean {
  return (
    line.level === row.level &&
    line.tag === row.tag &&
    line.pid === row.pid &&
    line.message === row.message
  );
}

function isDup(a: LogLine, b: LogLine): boolean {
  return (
    a.level === b.level &&
    a.tag === b.tag &&
    a.pid === b.pid &&
    a.message === b.message
  );
}

/**
 * Map a source-array index (raw `LogLine[]`) to the displayed-array index
 * (aggregated `DisplayRow[]`). Used when the event drawer / search wants
 * to scroll to a specific raw line but the view is showing aggregated
 * rows. Returns -1 when the source index is out of range of any row.
 */
export function mapRawIdxToDisplayIdx(rawIdx: number, rows: DisplayRow[]): number {
  if (rawIdx < 0 || rows.length === 0) return -1;
  // Binary search on srcStart since srcStart is monotonically non-decreasing.
  let lo = 0;
  let hi = rows.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const r = rows[mid];
    if (rawIdx < r.srcStart) hi = mid - 1;
    else if (rawIdx > r.srcEnd) lo = mid + 1;
    else return mid;
  }
  return -1;
}
